import React, { memo, useCallback } from 'react';

import { Box, Tooltip, Typography } from '@mui/material';
import { NoteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { formatGuidelineCitationText } from 'constants/aiChat';

import { GUIDELINE_CITATION_BADGE_LABELS } from './GuidelineCitationBadge.constants';
import {
  badgeContainerStyles,
  iconStyles,
  mutedBadgeContainerStyles,
  mutedIconStyles,
  mutedTextStyles,
  textStyles,
} from './GuidelineCitationBadge.styles';
import { GuidelineCitationBadgeTestIds } from './GuidelineCitationBadge.testIds';

// LEN-213: an unresolved citation renders as a neutral, non-interactive chip showing
// `fallbackLabel` (a plain section label) — while loading (`isPending`) or when its
// details could not be loaded (`isUnavailable`) — never as a raw marker.
const GuidelineCitationBadge = ({
  citation,
  onClick,
  fallbackLabel = null,
  isPending = false,
  isUnavailable = false,
}) => {
  const isNeutral = isPending || isUnavailable;

  const onBadgeClick = useCallback(() => {
    onClick?.(citation);
  }, [citation, onClick]);

  const onBadgeKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.(citation);
      }
    },
    [citation, onClick],
  );

  if (isNeutral) {
    const neutralTooltip = isUnavailable
      ? GUIDELINE_CITATION_BADGE_LABELS.UNAVAILABLE
      : GUIDELINE_CITATION_BADGE_LABELS.LOADING;

    return (
      <Tooltip arrow placement="top" title={neutralTooltip}>
        <Box
          aria-label={`${fallbackLabel ?? ''} — ${neutralTooltip}`}
          component="span"
          data-testid={
            isUnavailable
              ? GuidelineCitationBadgeTestIds.UNAVAILABLE
              : GuidelineCitationBadgeTestIds.PENDING
          }
          sx={mutedBadgeContainerStyles}
        >
          <NoteIcon sx={mutedIconStyles} />
          <Typography sx={mutedTextStyles} variant="regular12">
            {fallbackLabel}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  const citationText = formatGuidelineCitationText(citation);
  const tooltipTitle = `${citation.source} - ${citation.title}`;

  return (
    <Tooltip arrow placement="top" title={tooltipTitle}>
      <Box
        aria-label={`View guideline: ${tooltipTitle}`}
        component="span"
        data-testid={GuidelineCitationBadgeTestIds.BADGE}
        role="button"
        sx={badgeContainerStyles}
        tabIndex={0}
        onClick={onBadgeClick}
        onKeyDown={onBadgeKeyDown}
      >
        <NoteIcon sx={iconStyles} />
        <Typography sx={textStyles} variant="regular12">
          {citationText}
        </Typography>
      </Box>
    </Tooltip>
  );
};

GuidelineCitationBadge.propTypes = {
  citation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
  }).isRequired,
  fallbackLabel: PropTypes.string,
  isPending: PropTypes.bool,
  isUnavailable: PropTypes.bool,
  onClick: PropTypes.func,
};

export default memo(GuidelineCitationBadge);
